import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createWidget } from 'actions/editor';
import { fetchComponentsForPalette, fetchPaletteComponent } from 'actions/palette';

import _ from 'lodash';
import cx from 'classnames';
import { IKImage } from 'imagekitio-react';
import { Frame } from "scenejs";

import { PaletteContext } from '.';
import InfiniteScroll from "components/InfiniteScroll";
import { getBoundingRectFromLayout } from 'components/Editor/helper/layoutManager';

import { ASSET, BUTTON, CAROUSEL, CONTENT, CONTENT_CARD, CONTENT_FILTER, CONTENT_FILTER_CONTAINER, CONTENT_SEARCH, FORM_FIELD, HOTSPOT, HTML, IMAGE, LINE, PALETTE_COMPONENT, SECTION, SECTION_COLUMN, SHAPE } from "constants/widgetTypes";

import styles from './palette.module.scss';
import { VIDEO } from 'constants/widgetAssetTypes';
import StoryboardContext from 'components/Storyboard/context';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { createMixpanelEvent } from 'actions/mixpanel';
import { MIXPANEL_BRANDKIT_CREATED, MIXPANEL_WIDGET_USED } from 'constants/mixpanelEvents';
  
  export const PaletteComponentCard = ({ editorSession, defaultParent, component, typeName }) => {
    const dispatch = useDispatch();
    const paletteContext = React.useContext(PaletteContext);
    const storyboardContext = React.useContext(StoryboardContext);
    const storyboardType = _.get(storyboardContext, 'type');
  
    const creating = React.useRef(false);
    const element = useSelector(state => _.get(state, `palette.paletteComponent.${component.id}.item`, {}));
    const loading = useSelector(state => _.get(state, `palette.paletteComponent.${component.id}.loading`, false));
    const customFormFields = useSelector((state) => _.get(state, "formFields.items", null));
  
    const icon = _.get(component, 'icon.url', null);
  
    useEffect(() => {
      if (element && element.id === component.id && creating.current) {
        creating.current = false;
  
        var request = _.get(element, 'content.widgets', []);
  
        request = request.filter(widget => !_.includes([PALETTE_COMPONENT], widget.type));
        
        var updatedWidgets = updateFormFieldWidgets(request, customFormFields);
        var preprocessedWidgets = prepareCopyRequest(updatedWidgets)
        var cleanWidgets =  preprocessedWidgets.map(widget => _cleanData(widget))
        var finalWidgets = preparePasteRequest(cleanWidgets);

        if (_.size(finalWidgets) == 0) {
            paletteContext.close();
            return;
          }
  
        dispatch(createWidget(editorSession, finalWidgets, defaultParent));


          var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
          mixpanelPayload = {
            ...mixpanelPayload,
            widget_type: (typeName == undefined && ["STATIC_CARDS", "INLINE_CARDS", "ASSET_GRID"].includes(component.type)) ? "Asset Grid" : formatString(typeName),
            brandkit_widget: !component.systemDefined
          }
          dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))


        paletteContext.close();
      }
    }, [element]);
  
     const handleCreateSection = (component) => {
      console.log("Create section clicked")
      creating.current = true;
  
      const id = _.get(component, 'id', "");
      dispatch(fetchPaletteComponent(id));
    }
  
    const pasteStateReference = React.useRef({
      page: null,
      scrollPosition: null,
      offset: 1
    });
  
  
    const _cleanData = (widget) => {
      const children = widget.widgets;
      if (_.size(children) > 0) {
        widget.widgets = children.map(child => _cleanData(child));
      }
  
      return _.omit(widget, ['id', 'uid', 'parent', 'template']);
    }

    const updateFormFieldWidgets = (widgets, customFormFields) => {
      let fields = customFormFields.filter(field => !field.userDefined);
    
      let formFieldWidgets = widgets.filter(widget => _.includes([FORM_FIELD], widget.type));
    
      let updatedArray = _.cloneDeep(formFieldWidgets);
    
      _.forEach(updatedArray, widget => {
        let matchingWidget = _.find(fields, { name: widget.name });
        if (matchingWidget) {
          _.set(widget, 'content', matchingWidget.id);
        }
      });
    
      let filteredWidgets = widgets.filter(widget => !_.includes([FORM_FIELD], widget.type));
    
      let resultArray = [...filteredWidgets, ...updatedArray];
    
      return resultArray;
    }
  
    const prepareCopyRequest = (widgets) => {
  
      const _findWidget = (id) => {
        return widgets.find(widget => widget.uid === id);
      }
  
      const _findChildren = (parent) => {
        return widgets.filter(widget => widget.parent === parent);
      }
  
      const reCalculateWidgetLayout = (widget, layout, breakpoint) => {
        var wRect = getBoundingRectFromLayout(layout);
  
        var parent = _findWidget(widget.parent);
  
        const frame = new Frame(layout);
  
        var pRectX = 0
        var pRectY = 0
  
        var x = wRect.x + pRectX;
        var y = wRect.y + pRectY;
  
        const translate = `${x}px, ${y}px`;
  
        frame.set("transform", "translate", translate);
        var updatedLayout = frame.get();

        if (parent) {
          return reCalculateWidgetLayout(parent, updatedLayout, breakpoint);
        }
  
        return updatedLayout;
      }
  
      const _buildCopyRequest = (widget, calculateLayout) => {
        var widgetRequest = _.cloneDeep(widget);
        var children = _findChildren(widget.uid);
        var parent = _findWidget(widget.parent);
  
        if (parent) {
          let desktopLayout = reCalculateWidgetLayout(widget, _.get(widget, `properties.desktop.layout`), 'desktop');
  
          let mobileLayout = reCalculateWidgetLayout(widget, _.get(widget, `properties.mobile.layout`), 'mobile');
  
          widgetRequest = {
            ...widgetRequest,
            properties: {
              ...widgetRequest.properties,
              desktop: {
                ...widgetRequest.properties.desktop,
                layout: desktopLayout
              },
              mobile: {
                ...widgetRequest.properties.mobile,
                layout: mobileLayout
              }
            }
          }
        }
  
        if (_.size(children) > 0) {
          widgetRequest.widgets = children
            .map(child => _buildCopyRequest(child, false))
            .sort((w1, w2) => {
              const o1 = _.get(w1, 'order') || 1;
              const o2 = _.get(w2, 'order') || 1;
  
              return o1 - o2;
            });
        }

        if( [HTML].includes(widgetRequest.type) && widgetRequest.content && widgetRequest.content.processedHtml){
          delete widgetRequest.content.processedHtml;
        }
  
        return widgetRequest;
      }
  
      const selected = widgets.filter(widget => !_findWidget(widget.parent));
  
      var processedWidgets = selected.filter(widget=> ![SECTION_COLUMN, HOTSPOT].includes(widget.type)).map(widget => _buildCopyRequest(widget, true));
  
      return processedWidgets;
    };
  
  
    const preparePasteRequest = (widgets) => {
      const scrollContainer = document.querySelector(`#${editorSession} .editor_widget_scroll_container`);
      const top = scrollContainer.scrollTop;
      const left = scrollContainer.scrollLeft;
      
      const originalTop = 0;
      const originalLeft = 0;
    
      var offset = pasteStateReference.current.offset;
    
      var ignoreTypes = [];
      
      widgets = widgets.filter(item => !_.includes(ignoreTypes, item.type));
    
      widgets = widgets.map(widget => {
        let children = _.get(widget, 'widgets', null);
        let hotspot = children && children.find(item => _.isEqual(HOTSPOT, item.type));
      
        if(children != null && !hotspot) {
          children = children.map(childwidget => {
            var actions = _.get(childwidget, 'actions');
            if(actions !== null && _.size(actions) > 0 && !_.includes(['link', 'pageAnchor'], actions[0].type)) {
                const updatedWidget = {
                  ...childwidget,
                  actions: []
                }
    
                return updatedWidget;
            }
            return childwidget;
          })
    
          widget = {
            ...widget,
            widgets: children
          }
          return widget;
    
        } else {
          var actions = _.get(widget, 'actions');  
          if(actions !== null && _.size(actions) > 0 && !_.includes(['link', 'pageAnchor'], actions[0].type)) {
              widget = {
                ...widget,
                actions: []
              }
          }
          return widget;
        }
      })
    
      if(_.size(widgets) === 0){
        return null;
      }
    
      var processedWidgets = widgets.map(widget => {
        var layout = _.get(widget, 'properties.desktop.layout') || {};
        var stretched = _.get(widget, 'properties.desktop.stretched') || null;
        var inlineStretched = _.get(widget, 'properties.desktop.display') || null;
        const positon = new Frame(layout);
        const value = positon.get("transform", "translate").split(",").map(str => {
          str = str.trim();
          return parseFloat(str);
        });
    
        if(widget.type === ASSET){
          widget.content = null;
          
          if(widget.properties.desktop.type && _.includes(["VIDEO"], widget.properties.desktop.type)){
            widget.properties.desktop.video = null;
          }
    
          if(widget.properties.desktop.background && _.includes(["image", "video"], widget.properties.desktop.background.type)){
            widget.properties.desktop.background = null;
          }
        }
    
        if(widget.type === CONTENT){
          delete widget.id;
          widget.content = {};
    
          widget.properties.desktop.filters = false;
          widget.properties.desktop.search = false;
          widget.properties.mobile.filters = false;
          widget.properties.mobile.search = false;
    
          widget.properties.desktop.layoutStyle.itemsPerPage = 3;
          
          var contentWidgets = widget.widgets.filter(item => !_.includes([CONTENT_CARD], item.type));
    
          contentWidgets.map(child => {
            if([CONTENT_FILTER].includes(child.type)){
                var filterChildren = child.widgets.map(filterChild => {
                  if(filterChild.type === CONTENT_FILTER_CONTAINER){
                    filterChild.content = [];
                  }
                  if(filterChild.type === CONTENT_SEARCH){
                    filterChild.content = null
                  }
                  return filterChild;
                });
                child.widgets = filterChildren;
              }
            })
    
          widget.widgets = contentWidgets;
    
          if(widget.properties.desktop.background && _.includes(["image", "video"], widget.properties.desktop.background.type)){
            widget.properties.desktop.background = null;
          }
        }
    
        isAssetIsChild(widget);
    
        const x = value[0] - originalLeft + left;
        const y = value[1] - originalTop + top  + 20 * offset;
        positon.set('transform', 'translate', `${x}px, ${y}px`);
    
        widget = _.set(widget, 'properties.desktop.layout', positon.get());

        var width = widget.properties.desktop.layout.width;

        if(width && typeof width === 'object' && Object.keys(width).length === 0 && width.constructor === Object){
          delete widget.properties.desktop.layout.width;
        }
      
        if ( _.includes(["INTERACTIVE_CONTENT", "SPATIAL"], storyboardType) && stretched) {
          widget.properties.desktop.stretched = false;
          widget.properties.desktop.layout.width = '1200px';
        }
  
        delete widget.id;
        return widget;
    
      }).sort((w1, w2) => {
        const o1 = _.get(w1, 'order') || 1;
        const o2 = _.get(w2, 'order') || 1;
    
        return o1 - o2;
      });
    
      return processedWidgets;
    }
  
    function isAssetIsChild(widget) {
      const widgetTypesWithAsset = [SECTION, CAROUSEL, IMAGE, VIDEO, BUTTON, SHAPE, LINE];
    
      if (widgetTypesWithAsset.includes(widget?.type)) {
        widget?.widgets?.map(filterChild => {
          var filterChildren = filterChild?.widgets?.map(innerChild => {
            if (innerChild?.type === ASSET) {
              innerChild.content = null;
            }
            return innerChild;
          });
          filterChild.widgets = filterChildren;
        });
      }
    }

    const formatString = (value) => {
      if (value) {
        value = value.replace(/_/g, " ");
  
        value = value
          .toLowerCase()
          .split(" ")
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }
      return value;
    };
  
    return (
        <div className={styles.widget} onClick={() => handleCreateSection(component)}>
          <div className={styles.iconContainer}>
            {icon ? (
              <IKImage className={styles.icon} path={icon} />
            ) : (
              <img
                className={styles.icon}
                onClick={() => handleCreateSection(component)}
                src="/images/storyboard.png"
                alt=""
              />
            )}
      
            {loading && (
              <div className={styles.loader}>
                <div className={styles.inner}>Adding element...</div>
              </div>
            )}
          </div>
        </div>
      );
  }
